import { useQuery } from '@tanstack/react-query';
import { getTodos } from '../../nucleo/api';
import { useSessao } from '../../nucleo/sessao';
import type { Atividade } from './tipos';

/**
 * Lista B de 30/10 (itens 27 a 29): a reunião é um agendamento da Agenda. O Pipeline lê só o necessário pela API da Agenda
 * (sem importar o módulo): quem não vê a Agenda, ou sem o módulo instalado, não recebe reuniões em nenhuma lista.
 */
export interface ReuniaoAgenda {
  id: string;
  assunto: string | null;
  servico_nome: string | null;
  parceiro_nome: string | null;
  oportunidade_id: string | null;
  inicio: string;
  fim: string;
  estado: 'marcado' | 'confirmado' | 'realizado' | 'faltou' | 'cancelado';
  /** Reunião livre de dia inteiro (03/11): sem hora para mostrar. */
  dia_inteiro?: boolean;
}

export const ESTADOS_REUNIAO_ABERTA: ReuniaoAgenda['estado'][] = ['marcado', 'confirmado'];

/** Só quem vê a Agenda (e com o módulo instalado) vê as reuniões. */
export function useVeReunioes(): boolean {
  const s = useSessao();
  return s.instalado('agenda') && s.pode('agenda.ver');
}

/**
 * Remove as reuniões que já são uma atividade da negociação (`agendamento_id`, item 23): a atividade é a linha única.
 * Função pura, testada.
 */
export function reunioesSemAtividade<R extends { id: string }>(reunioes: readonly R[], atividades: readonly Pick<Atividade, 'agendamento_id'>[]): R[] {
  const ligadas = new Set(atividades.map((a) => a.agendamento_id).filter((x): x is string => !!x));
  return reunioes.filter((r) => !ligadas.has(r.id));
}

/** Título da reunião: o assunto ou, sem ele, o serviço (tipo de reunião). Vazio quando nenhum dos dois. */
/** Quem exibe passa o resultado por `textoLivre`. */
export const tituloDaReuniao = ({ assunto, servico_nome: servico }: Pick<ReuniaoAgenda, 'assunto' | 'servico_nome'>) => assunto?.trim() || servico?.trim() || '';

/** Reuniões ligadas à negociação (todas, com canceladas; a aba filtra por situação). Desligada sem a Agenda. */
export function useReunioesDaNegociacao(oportunidadeId: string) {
  const ve = useVeReunioes();
  return useQuery({
    queryKey: ['/agenda/agendamentos', 'pipeline', 'oportunidade', oportunidadeId],
    queryFn: () => getTodos<ReuniaoAgenda>('/agenda/agendamentos', { oportunidade_id: oportunidadeId, cancelados: 'true' }),
    enabled: ve,
  });
}
